import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import OutlinedInput from "@mui/material/OutlinedInput";
import { toast } from "react-toastify";
import QuillEditor from "../../components/QuillEditor";
import api from "../../utils/api";

const languages = ["en", "hy", "ru"];
const languageLabels = { en: "English", hy: "Armenian", ru: "Russian" };

const getError = (errorsObj, field) => {
  const v = errorsObj[field];
  if (Array.isArray(v) && v.length) return v[0];
  if (typeof v === "string") return v;
  return "";
};

export default function Terms() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [title, setTitle] = useState({ title_hy: "", title_en: "", title_ru: "" });
  const [description, setDescription] = useState({
    description_hy: "",
    description_en: "",
    description_ru: "",
  });
  const [metaKeywords, setMetaKeywords] = useState({
    meta_keywords_hy: "",
    meta_keywords_en: "",
    meta_keywords_ru: "",
  });
  const [metaDescription, setMetaDescription] = useState({
    meta_description_hy: "",
    meta_description_en: "",
    meta_description_ru: "",
  });
  const descriptionEditorRefs = useRef({});

  const loadTerms = () => {
    return api.get("admin/content/terms").then((res) => {
      const d = res.data?.data || {};
      const terms = d.terms || {};
      setTitle({
        title_hy: terms.title_hy || "",
        title_en: terms.title_en || "",
        title_ru: terms.title_ru || "",
      });
      setDescription({
        description_hy: terms.description_hy ?? "",
        description_en: terms.description_en ?? "",
        description_ru: terms.description_ru ?? "",
      });
      setMetaKeywords({
        meta_keywords_hy: terms.meta_keywords_hy ?? "",
        meta_keywords_en: terms.meta_keywords_en ?? "",
        meta_keywords_ru: terms.meta_keywords_ru ?? "",
      });
      setMetaDescription({
        meta_description_hy: terms.meta_description_hy ?? "",
        meta_description_en: terms.meta_description_en ?? "",
        meta_description_ru: terms.meta_description_ru ?? "",
      });
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadTerms()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleTitleChange = (lang, value) => {
    setTitle((prev) => ({ ...prev, [`title_${lang}`]: value }));
    setErrors((prev) => ({ ...prev, [`title_${lang}`]: undefined }));
  };

  const handleDescriptionChange = (lang, value) => {
    setDescription((prev) => ({ ...prev, [`description_${lang}`]: value }));
    setErrors((prev) => ({ ...prev, [`description_${lang}`]: undefined }));
  };

  const handleMetaKeywordsChange = (lang, value) => {
    setMetaKeywords((prev) => ({ ...prev, [`meta_keywords_${lang}`]: value }));
    setErrors((prev) => ({ ...prev, [`meta_keywords_${lang}`]: undefined }));
  };

  const handleMetaDescriptionChange = (lang, value) => {
    setMetaDescription((prev) => ({ ...prev, [`meta_description_${lang}`]: value }));
    setErrors((prev) => ({ ...prev, [`meta_description_${lang}`]: undefined }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setErrors({});
    try {
      const getDesc = (lang) => {
        const ref = descriptionEditorRefs.current[lang];
        if (ref && typeof ref.getContent === "function") {
          const content = ref.getContent();
          if (content !== undefined && content !== null) return content;
        }
        return description[`description_${lang}`] ?? "";
      };
      const payload = {
        title_hy: title.title_hy,
        title_en: title.title_en,
        title_ru: title.title_ru,
        description_hy: getDesc("hy"),
        description_en: getDesc("en"),
        description_ru: getDesc("ru"),
        meta_keywords_hy: metaKeywords.meta_keywords_hy,
        meta_keywords_en: metaKeywords.meta_keywords_en,
        meta_keywords_ru: metaKeywords.meta_keywords_ru,
        meta_description_hy: metaDescription.meta_description_hy,
        meta_description_en: metaDescription.meta_description_en,
        meta_description_ru: metaDescription.meta_description_ru,
      };
      await api.put("admin/content/terms", payload);
      await loadTerms();
      setErrors({});
      toast.success("Terms & Conditions saved successfully.");
    } catch (e) {
      if (e?.errors && typeof e.errors === "object") setErrors(e.errors);
      const message = e?.errors
        ? "Please fix the validation errors."
        : (e?.response?.data?.message || "Failed to save terms.");
      toast.error(message);
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography component="h2" variant="h5" sx={{ my: 3, borderBottom: "1px solid", borderColor: "divider", pb: 1 }}>
        Terms & Conditions
      </Typography>

      <Box sx={{ width: "100%", minWidth: 0 }}>
        <FormControl fullWidth error={!!getError(errors, "title_hy") || !!getError(errors, "title_en") || !!getError(errors, "title_ru")}>
          {languages.map((lang) => (
            <Box key={lang} sx={{ mb: 1.5 }}>
              <FormLabel sx={{ display: "block", mb: 0.5 }} htmlFor={`terms-title_${lang}`}>Title ({languageLabels[lang]})</FormLabel>
              <OutlinedInput
                id={`terms-title_${lang}`}
                fullWidth
                size="small"
                value={title[`title_${lang}`] || ""}
                onChange={(e) => handleTitleChange(lang, e.target.value)}
              />
              {getError(errors, `title_${lang}`) && (
                <FormHelperText sx={{ m: 0.5 }} error>{getError(errors, `title_${lang}`)}</FormHelperText>
              )}
            </Box>
          ))}
        </FormControl>
      </Box>

      <Typography component="h2" variant="h5" sx={{ my: 3, borderBottom: "1px solid", borderColor: "divider", pb: 1 }}>
        Content
      </Typography>

      <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
        {languages.map((lang) => (
          <Box key={lang} sx={{ mb: 3 }}>
            <FormLabel sx={{ display: "block", mb: 0.5 }}>Description ({languageLabels[lang]})</FormLabel>
            <QuillEditor
              key={`terms-desc-${lang}`}
              ref={(r) => { descriptionEditorRefs.current[lang] = r; }}
              value={description[`description_${lang}`] || ""}
              onChange={(html) => handleDescriptionChange(lang, html)}
              placeholder="Terms content..."
            />
            {getError(errors, `description_${lang}`) && (
              <FormHelperText error sx={{ mt: 0.5 }}>{getError(errors, `description_${lang}`)}</FormHelperText>
            )}
          </Box>
        ))}
      </Box>

      <Typography component="h2" variant="h5" sx={{ my: 3, borderBottom: "1px solid", borderColor: "divider", pb: 1 }}>
        Meta (SEO)
      </Typography>

      <Box sx={{ width: "100%", minWidth: 0 }}>
        {languages.map((lang) => (
          <Box key={lang} sx={{ mb: 2 }}>
            <FormLabel sx={{ display: "block", mb: 0.5 }} htmlFor={`terms-meta-kw-${lang}`}>Meta keywords ({languageLabels[lang]})</FormLabel>
            <OutlinedInput
              id={`terms-meta-kw-${lang}`}
              fullWidth
              size="small"
              value={metaKeywords[`meta_keywords_${lang}`] || ""}
              onChange={(e) => handleMetaKeywordsChange(lang, e.target.value)}
            />
            <FormLabel sx={{ display: "block", mb: 0.5, mt: 1 }} htmlFor={`terms-meta-desc-${lang}`}>Meta description ({languageLabels[lang]})</FormLabel>
            <OutlinedInput
              id={`terms-meta-desc-${lang}`}
              fullWidth
              size="small"
              value={metaDescription[`meta_description_${lang}`] || ""}
              onChange={(e) => handleMetaDescriptionChange(lang, e.target.value)}
            />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </Box>
    </Box>
  );
}
